import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  PhasedReviewDialog,
  type PhasedReviewDialogPhase,
} from "../VersionReviewDialog/PhasedReviewDialog";

import CreateCommitMessage from "./CreateCommitMessage";
import RunTests from "./RunTests";
import useNavigateToPromptVersion from "./hooks/useNavigateToPromptVersion";
import useRedirectLogic from "./hooks/useRedirectLogic";

import { PromptVersion } from "@/types/apiGetters";
import { PromptBlueprint } from "@/types";

const DialogContentView = ({
  submittedVersion,
  disableRedirect,
  redirect,
  shouldShowTests,
  data,
  promptName,
  onClose,
  onConfirm,
  isLoading,
  onGoBack,
  commitLabel,
}: {
  submittedVersion?: PromptVersion;
  disableRedirect: boolean;
  redirect: () => void;
  shouldShowTests: boolean;
  data?: PromptBlueprint;
  promptName?: string;
  onClose: () => void;
  onConfirm: (commitMessage?: string) => void;
  isLoading?: boolean;
  onGoBack?: () => void;
  commitLabel?: string;
}) => {
  if (submittedVersion && shouldShowTests && !disableRedirect) {
    return <RunTests submittedPromptVersion={submittedVersion} onClose={redirect} />;
  }

  if (!submittedVersion) {
    return (
      <CreateCommitMessage
        data={data}
        promptName={promptName}
        onClose={onClose}
        onConfirm={onConfirm}
        isLoading={isLoading}
        onGoBack={onGoBack}
        commitLabel={commitLabel}
      />
    );
  }

  return null;
};

interface CommitMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (commitMessage?: string) => void;
  title?: string;
  saveTitle?: string;
  data?: PromptBlueprint;
  prompt?: {
    name?: string;
    id?: number;
  };
  isLoading?: boolean;
  showGoBack?: boolean;
  onGoBack?: () => void;
  commitLabel?: string;
  submittedVersion?: PromptVersion;
  disableRedirect?: boolean;
  isSuccess?: boolean;
}

const SUCCESS_TRANSITION_DELAY_MS = 900;

const CommitMessageDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title = "Save New Prompt Version",
  saveTitle = "Prompt Template Saved",
  commitLabel = "Prompt version change message (Optional)",
  data,
  prompt,
  onGoBack,
  submittedVersion,
  disableRedirect = false,
  isSuccess = false,
}: CommitMessageDialogProps) => {
  const { shouldShowTestContent, link } = useRedirectLogic(prompt, submittedVersion);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [shouldShowTests, setShouldShowTests] = useState(false);

  const redirect = useNavigateToPromptVersion(submittedVersion!);
  const shouldShowSavingState = Boolean(isLoading && !submittedVersion && !isSuccess);
  const shouldShowSuccessState = Boolean(
    isSuccess || (submittedVersion && !shouldNavigate && !shouldShowTests),
  );
  let phase: PhasedReviewDialogPhase = "review";
  if (shouldShowSavingState) {
    phase = "saving";
  } else if (shouldShowSuccessState) {
    phase = "success";
  }

  useEffect(() => {
    if (!isOpen) {
      setShouldNavigate(false);
      setShouldShowTests(false);
      return undefined;
    }

    if (!submittedVersion) {
      setShouldNavigate(false);
      setShouldShowTests(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (disableRedirect) {
        onClose();
        return;
      }

      if (shouldShowTestContent) {
        setShouldShowTests(true);
        return;
      }

      setShouldNavigate(true);
    }, SUCCESS_TRANSITION_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [disableRedirect, isOpen, onClose, shouldShowTestContent, submittedVersion]);

  if (prompt && submittedVersion && isOpen && shouldNavigate) {
    return <Navigate to={link} replace />;
  }

  const dialogContent = (
    <DialogContentView
      submittedVersion={submittedVersion}
      disableRedirect={disableRedirect}
      redirect={redirect}
      shouldShowTests={shouldShowTests}
      data={data}
      promptName={prompt?.name}
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isLoading}
      onGoBack={onGoBack}
      commitLabel={commitLabel}
    />
  );

  return (
    <PhasedReviewDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      phase={phase}
      reviewContent={dialogContent}
      reviewContentStyle={{ width: "760px", maxWidth: "75vw" }}
      statusTitle={shouldShowSavingState ? "Saving version" : saveTitle}
      statusDescription={
        shouldShowSavingState
          ? "Creating a new version from your latest changes."
          : "Your version has been saved successfully."
      }
    />
  );
};

export default CommitMessageDialog;
